import React from 'react';
import { Link } from 'react-router-dom';
import { FaClock, FaGasPump, FaIdCard, FaMapMarkedAlt, FaRoute, FaTags } from 'react-icons/fa';

const Landing: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#0f1419] text-white">
      <header className="sticky top-0 z-40 bg-gradient-to-br from-[#1a2332] to-[#253547] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Abastece+" className="w-9 h-9 object-contain" />
            <span className="font-bold text-lg">Abastece+</span>
            <span className="hidden sm:inline text-[11px] px-2 py-0.5 rounded-full bg-[#089453]/15 text-[#84cc34]">
              para postos
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-white/80">
            <a href="#beneficios" className="hover:text-white">Benefícios</a>
            <a href="#como-funciona" className="hover:text-white">Como funciona</a>
            <a href="#faq" className="hover:text-white">FAQ</a>
            <Link to="/suporte" className="hover:text-white">Suporte</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="px-3 py-2 text-sm rounded-lg border border-white/15 hover:border-white/30 transition-colors"
            >
              Entrar
            </Link>
            <Link
              to="/cadastro-parceiro"
              className="px-4 py-2 text-sm rounded-lg bg-gradient-to-br from-[#089453] to-[#84cc34] text-white shadow-[0_4px_16px_rgba(8,148,83,0.3)] hover:shadow-[0_6px_24px_rgba(8,148,83,0.4)] transition-all"
            >
              Cadastrar meu posto
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <p className="text-[#84cc34] font-semibold mb-3">Portal do dono de posto</p>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
            Mostre seus preços para quem está procurando onde abastecer
          </h1>
          <p className="text-white/75 text-lg leading-relaxed mb-8">
            Motoristas usam o app Abastece+ para comparar preços de combustível e traçar a rota até o posto. Cadastre
            seu posto e publique os preços oficiais — direto da fonte, sempre atualizados.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/cadastro-parceiro"
              className="px-5 py-3 rounded-xl bg-gradient-to-br from-[#089453] to-[#84cc34] text-[#0f1419] font-semibold"
            >
              Criar conta do posto
            </Link>
            <a
              href="#como-funciona"
              className="px-5 py-3 rounded-xl bg-white/10 border border-white/10 hover:border-white/30 transition-colors"
            >
              Ver como funciona
            </a>
          </div>
        </div>
        <div className="bg-gradient-to-br from-[#1a2332] to-[#253547] border border-white/10 rounded-3xl p-8">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="p-4 rounded-2xl bg-black/20 border border-white/5">
              <FaTags className="text-[#84cc34] text-xl mb-2" />
              <p className="text-white/80">Preços oficiais do posto</p>
            </div>
            <div className="p-4 rounded-2xl bg-black/20 border border-white/5">
              <FaMapMarkedAlt className="text-[#84cc34] text-xl mb-2" />
              <p className="text-white/80">Seu posto no mapa</p>
            </div>
            <div className="p-4 rounded-2xl bg-black/20 border border-white/5">
              <FaRoute className="text-[#84cc34] text-xl mb-2" />
              <p className="text-white/80">Sugerido nas rotas</p>
            </div>
            <div className="p-4 rounded-2xl bg-black/20 border border-white/5">
              <FaClock className="text-[#84cc34] text-xl mb-2" />
              <p className="text-white/80">Atualize em segundos</p>
            </div>
          </div>
        </div>
      </section>

      <section id="beneficios" className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        <h2 className="text-2xl font-bold mb-6">Por que cadastrar seu posto</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-6 rounded-2xl bg-[#1a2332] border border-white/10">
            <FaGasPump className="text-[#84cc34] text-2xl mb-3" />
            <h3 className="font-semibold mb-2">Mais motoristas na bomba</h3>
            <p className="text-white/70">Quem busca combustível perto ou no caminho encontra o seu posto com preço.</p>
          </div>
          <div className="p-6 rounded-2xl bg-[#1a2332] border border-white/10">
            <FaTags className="text-[#84cc34] text-2xl mb-3" />
            <h3 className="font-semibold mb-2">Preço confiável</h3>
            <p className="text-white/70">Postos verificados publicam o preço direto, com a data da última atualização.</p>
          </div>
          <div className="p-6 rounded-2xl bg-[#1a2332] border border-white/10">
            <FaIdCard className="text-[#84cc34] text-2xl mb-3" />
            <h3 className="font-semibold mb-2">Você no controle</h3>
            <p className="text-white/70">Atualize preços, endereço e horário do posto quando quiser.</p>
          </div>
        </div>
      </section>

      <section id="como-funciona" className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        <h2 className="text-2xl font-bold mb-6">Como funciona</h2>
        <ol className="grid md:grid-cols-3 gap-6 text-white/75 list-decimal list-inside">
          <li className="p-6 rounded-2xl bg-[#1a2332] border border-white/10">Crie a conta do posto neste portal.</li>
          <li className="p-6 rounded-2xl bg-[#1a2332] border border-white/10">
            Solicite seu posto pelo CNPJ ou cadastre um novo. Nossa equipe confere e aprova.
          </li>
          <li className="p-6 rounded-2xl bg-[#1a2332] border border-white/10">
            Publique e atualize os preços sempre que mudarem.
          </li>
        </ol>
      </section>

      <section id="faq" className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        <h2 className="text-2xl font-bold mb-6">Perguntas frequentes</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl bg-[#1a2332] border border-white/10">
            <h3 className="font-semibold mb-2">Meu posto ainda não aparece no app. E agora?</h3>
            <p className="text-white/70">
              Ele pode já estar na nossa base, vindo do cadastro público da ANP. Crie sua conta e solicite o posto pelo
              CNPJ; depois da aprovação ele aparece no app e você publica os preços.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-[#1a2332] border border-white/10">
            <h3 className="font-semibold mb-2">Posso usar o aplicativo com a conta do posto?</h3>
            <p className="text-white/70">
              Não. A conta do posto acessa apenas este portal. Para usar o app como motorista, crie uma conta separada
              no aplicativo.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        <div className="rounded-3xl bg-gradient-to-br from-[#089453] to-[#84cc34] p-8 text-[#0f1419] flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Pronto para divulgar seus preços?</h2>
            <p className="text-[#0f1419]/80">Crie a conta do posto ou fale com nosso time de suporte.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/cadastro-parceiro" className="px-5 py-3 rounded-xl bg-[#0f1419] text-white">Criar conta</Link>
            <Link to="/login" className="px-5 py-3 rounded-xl bg-white/20 text-[#0f1419]">Entrar</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-white/60 text-sm">
          <span>© 2026 Abastece+. Todos os direitos reservados.</span>
          <div className="flex gap-4">
            <Link to="/politica-privacidade" className="hover:text-white">Política de Privacidade</Link>
            <Link to="/suporte" className="hover:text-white">Suporte</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
